export default class PipiPupuAPIClient {
	constructor(apiUrl, auth) {
		this.apiUrl = apiUrl;
		this.auth = auth;
	}

	request(method, resource, query, body, signal) {
		let fetchOptions = {
			method,
			headers: {
				'Content-type': body?.constructor?.name == 'File' ? 'application/octet-stream' : 'application/json',
			},
			signal,
		};
		if(this.auth && this.auth.token && this.auth.token.raw) fetchOptions.headers.authorization = `Bearer ${this.auth.token.raw}`;
		if(method == 'POST' || method == 'PUT' || method == 'PATCH')
			fetchOptions.body = body?.constructor?.name == 'File' ? body : JSON.stringify(body||null);
		const q = query && new URLSearchParams(query).toString();
		const url = `${this.apiUrl}/${resource}${q ? `?${q}` : ''}`;
		return fetch(url, fetchOptions).then(r => {
			return r.text().then(text => {
				if(!text) throw new Error('Empty response');
				let parsed;
				try {
					parsed = JSON.parse(text);
				} catch(e) {
					throw new Error(`${r.status != 200 ? `${r.status} ${r.statusText||''}` : ''}${(text||'').replace(/<[^>]+>/g, '')}`);
				}
				if(parsed?.error) {
					if(parsed.error.startsWith('Could not verify jwt')) {
						localStorage.removeItem('username');
						localStorage.removeItem('authToken');
						this.auth.setToken(null);
					}
					const apiErr = new Error(parsed.error);
					if(parsed.code) apiErr.code = parsed.code;
					if(parsed.email) apiErr.email = parsed.email;
					if(parsed.nextAllowedAt) apiErr.nextAllowedAt = parsed.nextAllowedAt;
					throw apiErr;
				}
				if(r.status != 200) throw new Error(text);
				return parsed;
			});
		});
	}

	post(resource, body) {
		return this.request('POST', resource, {}, body);
	}

	put(resource, body) {
		return this.request('PUT', resource, {}, body);
	}

	patch(resource, body) {
		return this.request('PATCH', resource, {}, body);
	}

	get(resource, query, signal) {
		return this.request('GET', resource, query, null, signal);
	}

	delete(resource, query)	{
		return this.request('DELETE', resource, query);
	}

	// Fetches a non-JSON resource (a CSV export) as raw text. request() always parses JSON, so
	// exports need their own path; errors still come back as JSON, hence the parse attempt.
	downloadText(resource, query) {
		const headers = {};
		if(this.auth?.token?.raw) headers.authorization = `Bearer ${this.auth.token.raw}`;
		const q = query && new URLSearchParams(query).toString();
		return fetch(`${this.apiUrl}/${resource}${q ? `?${q}` : ''}`, { headers }).then(r => r.text().then(text => {
			if(r.status != 200) {
				let parsed;
				try { parsed = JSON.parse(text); } catch(e) { parsed = null; }
				throw new Error(parsed?.error || `${r.status} ${r.statusText||''}`);
			}
			return text;
		}));
	}

	uploadFile(filename, blob) { return this.request('POST', 'file', {filename}, blob); }

	uploadFileWithProgress(filename, blob, onProgress, signal) {
		return new Promise((resolve, reject) => {
			const xhr = new XMLHttpRequest();
			xhr.open('POST', `${this.apiUrl}/file?${new URLSearchParams({ filename })}`);
			xhr.setRequestHeader('Content-Type', 'application/octet-stream');
			if(this.auth?.token?.raw)
				xhr.setRequestHeader('Authorization', `Bearer ${this.auth.token.raw}`);
			xhr.upload.addEventListener('progress', e => {
				if(e.lengthComputable) onProgress?.(Math.round(e.loaded / e.total * 100));
			});
			xhr.addEventListener('load', () => {
				try {
					const parsed = JSON.parse(xhr.responseText);
					if(parsed?.error) reject(new Error(parsed.error));
					else if(xhr.status !== 200) reject(new Error(`${xhr.status} ${xhr.statusText}`));
					else resolve(parsed);
				} catch(e) {
					reject(new Error(xhr.responseText || String(xhr.status)));
				}
			});
			xhr.addEventListener('error', () => reject(new Error('Upload failed')));
			signal?.addEventListener('abort', () => {
				xhr.abort();
				reject(new DOMException('Aborted', 'AbortError'));
			});
			xhr.send(blob);
		});
	}

	// Generic MinIO-backed upload: POST the blob to an API endpoint that stores it
	// and returns { url, ... }. Used by PipiPupuFileS3.
	uploadToS3(endpoint, blob, onProgress, signal) {
		return new Promise((resolve, reject) => {
			const xhr = new XMLHttpRequest();
			xhr.open('POST', `${this.apiUrl}/${endpoint}`);
			xhr.setRequestHeader('Content-Type', 'application/octet-stream');
			if(this.auth?.token?.raw)
				xhr.setRequestHeader('Authorization', `Bearer ${this.auth.token.raw}`);
			xhr.upload.addEventListener('progress', e => {
				if(e.lengthComputable) onProgress?.(Math.round(e.loaded / e.total * 100));
			});
			xhr.addEventListener('load', () => {
				try {
					const parsed = JSON.parse(xhr.responseText);
					if(parsed?.error) reject(new Error(parsed.error));
					else if(xhr.status !== 200) reject(new Error(`${xhr.status} ${xhr.statusText}`));
					else resolve(parsed);
				} catch(e) {
					reject(new Error(xhr.responseText || String(xhr.status)));
				}
			});
			xhr.addEventListener('error', () => reject(new Error('Upload failed')));
			signal?.addEventListener('abort', () => {
				xhr.abort();
				reject(new DOMException('Aborted', 'AbortError'));
			});
			xhr.send(blob);
		});
	}

	uploadThumbnail(blob, onProgress, signal) {
		return new Promise((resolve, reject) => {
			const xhr = new XMLHttpRequest();
			xhr.open('POST', `${this.apiUrl}/thumbnail`);
			xhr.setRequestHeader('Content-Type', 'application/octet-stream');
			if(this.auth?.token?.raw)
				xhr.setRequestHeader('Authorization', `Bearer ${this.auth.token.raw}`);
			xhr.upload.addEventListener('progress', e => {
				if(e.lengthComputable) onProgress?.(Math.round(e.loaded / e.total * 100));
			});
			xhr.addEventListener('load', () => {
				try {
					const parsed = JSON.parse(xhr.responseText);
					if(parsed?.error) reject(new Error(parsed.error));
					else if(xhr.status !== 200) reject(new Error(`${xhr.status} ${xhr.statusText}`));
					else resolve(parsed);
				} catch(e) {
					reject(new Error(xhr.responseText || String(xhr.status)));
				}
			});
			xhr.addEventListener('error', () => reject(new Error('Upload failed')));
			signal?.addEventListener('abort', () => {
				xhr.abort();
				reject(new DOMException('Aborted', 'AbortError'));
			});
			xhr.send(blob);
		});
	}
}
